<template>
  <div>
    <!-- Separadores por tipo de RGPD -->
    <div class="lc-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        class="lc-tab"
        :class="{ 'lc-tab--active': activeTipo === tab.value }"
        @click="setTipo(tab.value)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Stats -->
    <div class="lc-stats-grid" v-if="rendered">
      <div class="lc-stat-card" v-for="stat in stats" :key="stat.label">
        <div class="lc-stat-card__icon" :style="{ background: stat.gradient }">
          <i :class="stat.icon"></i>
        </div>
        <div class="lc-stat-card__body">
          <div class="lc-stat-card__value">{{ stat.value }}</div>
          <div class="lc-stat-card__label">{{ stat.label }}</div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="lc-card">
      <div class="lc-card__header">
        <div class="lc-card__header-left">
          <h4 class="lc-card__title">Consentimentos RGPD</h4>
          <span class="lc-badge">{{ total }} registos</span>
        </div>
        <div class="lc-card__header-right">
          <div class="lc-search">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              class="lc-search__input"
              type="search"
              placeholder="Nome, email, NIF ou quem recomendou..."
              v-model="searchQuery"
            />
          </div>
          <button class="lc-btn lc-btn--dark" @click="downloadCsv()">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Exportar
          </button>
        </div>
      </div>

      <div v-if="!rendered" class="lc-loading">
        <div class="lc-loading__spinner"></div>
        <p>A carregar consentimentos...</p>
      </div>

      <div v-else class="lc-table-wrap">
        <table class="lc-table">
          <thead>
            <tr>
              <th @click="sortBy('nome')" class="lc-th--sortable">
                Nome <i :class="sortIcon('nome')"></i>
              </th>
              <th @click="sortBy('email')" class="lc-th--sortable">
                Email <i :class="sortIcon('email')"></i>
              </th>
              <th @click="sortBy('nif')" class="lc-th--sortable">
                NIF <i :class="sortIcon('nif')"></i>
              </th>
              <th @click="sortBy('tipo')" class="lc-th--sortable">
                Tipo <i :class="sortIcon('tipo')"></i>
              </th>
              <th @click="sortBy('consentimento')" class="lc-th--sortable">
                Consentimento <i :class="sortIcon('consentimento')"></i>
              </th>
              <th @click="sortBy('data_hora')" class="lc-th--sortable">
                Data/Hora <i :class="sortIcon('data_hora')"></i>
              </th>
              <th @click="sortBy('ref_email')" class="lc-th--sortable">
                Email do ref <i :class="sortIcon('ref_email')"></i>
              </th>
              <th @click="sortBy('ref_telefone')" class="lc-th--sortable">
                Telefone do ref <i :class="sortIcon('ref_telefone')"></i>
              </th>
              <th>Assinatura</th>
              <th>PDF</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in queriedData" :key="row.id">
              <td class="lc-td--nome">{{ row.nome }}</td>
              <td class="lc-td--email">{{ row.email }}</td>
              <td class="lc-td--nif">{{ row.nif }}</td>
              <td>
                <span
                  :class="
                    row.tipo === 'credito'
                      ? 'lc-tag lc-tag--credito'
                      : 'lc-tag lc-tag--seguro'
                  "
                >
                  {{ row.tipo === "credito" ? "Crédito" : "Seguro" }}
                </span>
              </td>
              <td>
                <span
                  :class="
                    row.consentimento === 'yes'
                      ? 'lc-tag lc-tag--sim'
                      : 'lc-tag lc-tag--nao'
                  "
                >
                  {{ row.consentimento === "yes" ? "Sim" : "Não" }}
                </span>
              </td>
              <td class="lc-td--data">
                {{ formatData(row.data_hora || row.confirmado_em) }}
              </td>
              <td class="lc-td--email">
                <a v-if="row.ref_email" :href="'mailto:' + row.ref_email"
                   :title="'Recomendado por ' + (row.ref_nome || row.ref_code)">
                  {{ row.ref_email }}
                </a>
                <span v-else class="lc-empty-cell">—</span>
              </td>
              <td>
                <a v-if="row.ref_telefone" :href="'tel:' + row.ref_telefone">
                  {{ row.ref_telefone }}
                </a>
                <span v-else class="lc-empty-cell">—</span>
              </td>
              <td>
                <button
                  v-if="row.assinatura"
                  class="lc-icon-btn lc-icon-btn--green"
                  @click="verAssinatura(row)"
                  title="Ver assinatura"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </button>
                <span v-else class="lc-empty-cell">—</span>
              </td>
              <td>
                <button
                  v-if="row.pdf_path"
                  class="lc-icon-btn lc-icon-btn--blue"
                  @click="downloadPdf(row)"
                  title="Download PDF"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                <span v-else class="lc-empty-cell">—</span>
              </td>
            </tr>
            <tr v-if="queriedData.length === 0">
              <td colspan="10" class="lc-table__empty">
                Nenhum resultado encontrado.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="lc-card__footer">
        <p class="lc-pagination-info">
          Mostrando <strong>{{ from + 1 }}</strong
          >–<strong>{{ to }}</strong> de <strong>{{ total }}</strong>
        </p>
        <n-pagination
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        />
      </div>
    </div>

    <!-- Modal -->
    <transition name="lc-modal">
      <div
        class="lc-modal-overlay"
        v-if="modal.open"
        @click.self="modal.open = false"
      >
        <div class="lc-modal">
          <div class="lc-modal__header">
            <h5 class="lc-modal__title">Assinatura Digital</h5>
            <button class="lc-modal__close" @click="modal.open = false">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div class="lc-modal__body">
            <div class="lc-modal__sig-wrap">
              <img
                v-if="modal.src && modal.src.startsWith('data:')"
                :src="modal.src"
                class="lc-modal__sig-img"
                alt="Assinatura"
              />
              <img
                v-else-if="modal.src"
                :src="'https://leadcenter.pt/lead_center_v2_crm/' + modal.src"
                class="lc-modal__sig-img"
                alt="Assinatura"
              />
              <p
                v-else
                style="text-align: center; padding: 20px; color: #9ca3af"
              >
                Assinatura não disponível.
              </p>
            </div>
            <div class="lc-modal__info">
              <div class="lc-modal__info-item">
                <span class="lc-modal__info-label">Nome</span
                ><span class="lc-modal__info-value">{{ modal.nome }}</span>
              </div>
              <div class="lc-modal__info-item">
                <span class="lc-modal__info-label">NIF</span
                ><span
                  class="lc-modal__info-value"
                  style="font-family: monospace"
                  >{{ modal.nif }}</span
                >
              </div>
              <div class="lc-modal__info-item">
                <span class="lc-modal__info-label">Data</span
                ><span class="lc-modal__info-value">{{ modal.data }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import { VnudCard, Pagination as NPagination } from "@/components";
  import api from "@/api/index";
  import { json2excel } from "js2excel";

  export default {
    name: "DashboardRGPD",
    components: { VnudCard, NPagination },
    data() {
      return {
        rendered: false,
        dataTable: [],
        searchQuery: "",
        propsToSearch: ["nome", "email", "nif", "ref_email", "ref_telefone", "ref_nome"],
        pagination: { perPage: 25, currentPage: 1 },
        sortColumn: "data_hora",
        sortDir: "desc",
        modal: { open: false, src: "", nome: "", nif: "", data: "" },
        activeTipo: "todos",
        tabs: [
          { value: "todos", label: "Todos" },
          { value: "seguro", label: "Seguro" },
          { value: "credito", label: "Crédito" },
        ],
      };
    },
    methods: {
      async inicio() {
        // 'todos' não envia filtro; o backend devolve tudo
        const payload =
          this.activeTipo === "todos" ? {} : { tipo: this.activeTipo };
        const res = await api.listarConsentimentos(payload);
        if (res) this.dataTable = res.sort((a, b) => (a.id > b.id ? -1 : 1));
        this.rendered = true;
      },
      setTipo(tipo) {
        if (this.activeTipo === tipo) return;
        this.activeTipo = tipo;
        this.pagination.currentPage = 1;
        this.rendered = false;
        this.dataTable = [];
        this.inicio();
      },
      formatData(val) {
        if (!val) return "—";
        const d = new Date(val);
        if (isNaN(d)) return val;
        return (
          d.toLocaleDateString("pt-PT") +
          " " +
          d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })
        );
      },
      sortBy(col) {
        this.sortDir =
          this.sortColumn === col && this.sortDir === "asc" ? "desc" : "asc";
        this.sortColumn = col;
      },
      sortIcon(col) {
        if (this.sortColumn !== col)
          return "now-ui-icons arrows-1_minimal-up lc-sort-icon lc-sort-icon--off";
        return this.sortDir === "asc"
          ? "now-ui-icons arrows-1_minimal-up lc-sort-icon"
          : "now-ui-icons arrows-1_minimal-down lc-sort-icon";
      },
      verAssinatura(row) {
        this.modal = {
          open: true,
          src: row.assinatura,
          nome: row.nome,
          nif: row.nif,
          data: this.formatData(row.data_hora || row.confirmado_em),
        };
      },
      downloadPdf(row) {
        const a = document.createElement("a");
        a.href = "https://leadcenter.pt/lead_center_v2_crm/" + row.pdf_path;
        a.download = "RGPD_" + row.nif + ".pdf";
        a.target = "_blank";
        a.click();
      },
      downloadCsv() {
        const data = this.dataTable.map((r) => ({
          Nome: r.nome,
          Email: r.email,
          NIF: r.nif,
          Tipo: r.tipo === "credito" ? "Crédito" : "Seguro",
          Consentimento: r.consentimento === "yes" ? "Sim" : "Não",
          "Data/Hora": this.formatData(r.data_hora || r.confirmado_em),
          "Nome do ref": r.ref_nome || "",
          "Email do ref": r.ref_email || "",
          "Telefone do ref": r.ref_telefone || "",
          "Código do ref": r.ref_code || "",
        }));
        try {
          json2excel({ data, name: "RGPD_Consentimentos" });
        } catch (e) {
          alert("Erro ao exportar");
        }
      },
      getSorted() {
        const col = this.sortColumn,
          dir = this.sortDir === "asc" ? 1 : -1;
        return [...this.dataTable].sort((a, b) => {
          const va = (a[col] || "").toString().toLowerCase();
          const vb = (b[col] || "").toString().toLowerCase();
          return va < vb ? -dir : va > vb ? dir : 0;
        });
      },
    },
    mounted() {
      this.inicio();
    },
    computed: {
      stats() {
        const mes = new Date().getMonth() + 1;
        return [
          {
            label: "Total",
            value: this.dataTable.length,
            icon: "now-ui-icons ui-1_check",
            gradient: "linear-gradient(135deg,#4eab3e,#a9d42c)",
          },
          {
            label: "Este Mês",
            value: this.dataTable.filter(
              (r) =>
                parseInt(
                  (r.data_hora || r.confirmado_em || "").substr(5, 2),
                ) === mes,
            ).length,
            icon: "now-ui-icons ui-1_calendar-60",
            gradient: "linear-gradient(135deg,#2980b9,#6dd5fa)",
          },
          {
            label: "Consentiram",
            value: this.dataTable.filter((r) => r.consentimento === "yes")
              .length,
            icon: "now-ui-icons users_single-02",
            gradient: "linear-gradient(135deg,#4eab3e,#a9d42c)",
          },
          {
            label: "Não Consentiram",
            value: this.dataTable.filter((r) => r.consentimento !== "yes")
              .length,
            icon: "now-ui-icons ui-1_simple-remove",
            gradient: "linear-gradient(135deg,#e74c3c,#c0392b)",
          },
        ];
      },
      filteredData() {
        const q = this.searchQuery.toLowerCase();
        const sorted = this.getSorted();
        if (!q) return sorted;
        return sorted.filter((row) =>
          this.propsToSearch.some((k) =>
            (row[k] || "").toString().toLowerCase().includes(q),
          ),
        );
      },
      pagedData() {
        return this.filteredData.slice(this.from, this.to);
      },
      queriedData() {
        return this.pagedData;
      },
      from() {
        return this.pagination.perPage * (this.pagination.currentPage - 1);
      },
      to() {
        return Math.min(this.from + this.pagination.perPage, this.total);
      },
      total() {
        return this.filteredData.length;
      },
    },
  };
</script>

<style scoped>
  .lc-tabs {
    display: flex;
    gap: 6px;
    margin-bottom: 20px;
    background: #f3f4f6;
    padding: 5px;
    border-radius: 12px;
    width: fit-content;
  }
  .lc-tab {
    border: none;
    background: transparent;
    padding: 8px 20px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
  }
  .lc-tab:hover {
    color: #111318;
  }
  .lc-tab--active {
    background: #fff;
    color: #111318;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  .lc-tag--seguro {
    background: rgba(52, 152, 219, 0.1);
    color: #2980b9;
  }
  .lc-tag--credito {
    background: rgba(155, 89, 182, 0.12);
    color: #8e44ad;
  }
  .lc-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }
  @media (max-width: 768px) {
    .lc-stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  .lc-stat-card {
    background: #fff;
    border-radius: 14px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s, transform 0.2s;
  }
  .lc-stat-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.09);
    transform: translateY(-2px);
  }
  .lc-stat-card__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 20px;
    flex-shrink: 0;
  }
  .lc-stat-card__value {
    font-size: 26px;
    font-weight: 700;
    color: #111318;
    line-height: 1;
  }
  .lc-stat-card__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #9ca3af;
    margin-top: 4px;
  }
  .lc-card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 24px;
    overflow: hidden;
  }
  .lc-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 18px 24px;
    border-bottom: 1px solid #f0f0f0;
  }
  .lc-card__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .lc-card__header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .lc-card__title {
    font-size: 14px;
    font-weight: 700;
    color: #111318;
    margin: 0;
  }
  .lc-card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 24px;
    border-top: 1px solid #f0f0f0;
  }
  .lc-badge {
    background: rgba(78, 171, 62, 0.1);
    color: #357a28;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
  }
  .lc-search {
    position: relative;
    display: flex;
    align-items: center;
  }
  .lc-search svg {
    position: absolute;
    left: 10px;
    color: #9ca3af;
    pointer-events: none;
  }
  .lc-search__input {
    padding: 8px 12px 8px 32px;
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    color: #111318;
    outline: none;
    transition: border-color 0.2s;
    width: 240px;
  }
  .lc-search__input:focus {
    border-color: #4eab3e;
    box-shadow: 0 0 0 3px rgba(78, 171, 62, 0.1);
  }
  .lc-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }
  .lc-btn--dark {
    background: #111318;
    color: #fff;
  }
  .lc-btn--dark:hover {
    background: #2d3139;
    transform: translateY(-1px);
  }
  .lc-table-wrap {
    overflow-x: auto;
  }
  .lc-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 13px;
  }
  .lc-table thead th {
    padding: 11px 16px;
    background: #f8f9fb;
    color: #9ca3af;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    text-align: left;
  }
  .lc-th--sortable {
    cursor: pointer;
    user-select: none;
  }
  .lc-th--sortable:hover {
    color: #4eab3e;
  }
  .lc-table tbody td {
    padding: 11px 16px;
    border-bottom: 1px solid #f3f4f6;
    color: #3d4148;
    vertical-align: middle;
  }
  .lc-table tbody tr:last-child td {
    border-bottom: none;
  }
  .lc-table tbody tr:hover td {
    background: #f8fdf7;
  }
  .lc-td--nome {
    font-weight: 500;
  }
  .lc-td--email {
    color: #6b7280;
    font-size: 12px;
  }
  .lc-td--nif {
    font-family: monospace;
    color: #9ca3af;
    font-size: 12px;
  }
  .lc-td--data {
    color: #9ca3af;
    font-size: 12px;
    white-space: nowrap;
  }
  .lc-table__empty {
    text-align: center;
    color: #9ca3af;
    padding: 40px !important;
  }
  .lc-empty-cell {
    color: #d1d5db;
  }
  .lc-sort-icon {
    font-size: 10px;
    margin-left: 4px;
    color: #4eab3e;
  }
  .lc-sort-icon--off {
    color: #d1d5db;
  }
  .lc-tag {
    display: inline-block;
    padding: 2px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
  }
  .lc-tag--sim {
    background: rgba(78, 171, 62, 0.1);
    color: #357a28;
  }
  .lc-tag--nao {
    background: rgba(192, 57, 43, 0.08);
    color: #c0392b;
  }
  .lc-icon-btn {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
  }
  .lc-icon-btn--green {
    background: rgba(78, 171, 62, 0.1);
    color: #357a28;
  }
  .lc-icon-btn--blue {
    background: rgba(52, 152, 219, 0.1);
    color: #2980b9;
  }
  .lc-icon-btn--green:hover {
    background: rgba(78, 171, 62, 0.2);
    transform: translateY(-1px);
  }
  .lc-icon-btn--blue:hover {
    background: rgba(52, 152, 219, 0.2);
    transform: translateY(-1px);
  }
  .lc-pagination-info {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
  }
  .lc-loading {
    text-align: center;
    padding: 60px;
    color: #9ca3af;
  }
  .lc-loading__spinner {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 3px solid #e5e7eb;
    border-top-color: #4eab3e;
    animation: lc-spin 0.7s linear infinite;
    margin: 0 auto 12px;
  }
  @keyframes lc-spin {
    to {
      transform: rotate(360deg);
    }
  }
  .lc-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
  }
  .lc-modal {
    background: #fff;
    border-radius: 18px;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }
  .lc-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid #e5e7eb;
  }
  .lc-modal__title {
    font-size: 15px;
    font-weight: 700;
    color: #111318;
    margin: 0;
  }
  .lc-modal__close {
    background: none;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    padding: 4px;
    border-radius: 6px;
    display: flex;
    transition: all 0.2s;
  }
  .lc-modal__close:hover {
    background: #f3f4f6;
    color: #111318;
  }
  .lc-modal__body {
    padding: 24px;
  }
  .lc-modal__sig-wrap {
    background: #f8f9fb;
    border: 1.5px dashed #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 16px;
  }
  .lc-modal__sig-img {
    width: 100%;
    display: block;
  }
  .lc-modal__info {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .lc-modal__info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    padding: 8px 12px;
    background: #f8f9fb;
    border-radius: 7px;
  }
  .lc-modal__info-label {
    color: #9ca3af;
    font-weight: 500;
  }
  .lc-modal__info-value {
    color: #111318;
    font-weight: 600;
  }
  .lc-modal-enter-active,
  .lc-modal-leave-active {
    transition: opacity 0.25s ease;
  }
  .lc-modal-enter-from,
  .lc-modal-leave-to {
    opacity: 0;
  }
</style>
